import * as Sentry from '@sentry/react';
import { getSupabaseClient } from '@/integrations/supabase/client';
import type {
  KitchenTicket,
  KitchenTicketItem,
  KitchenTicketItemComponent,
  KitchenTicketItemStatus,
  KitchenTicketStatus,
} from '@/types/kitchen';
import {
  deriveTicketStatus,
  isKitchenTicketActiveOnBoard,
  itemStatusUpdatePayload,
  NEXT_STATUS,
  PREVIOUS_STATUS,
  ticketStatusUpdatePayload,
} from '@/services/kitchen/kitchenTicketStatus';

const KITCHEN_TICKET_SELECT = `
  id,
  tab_id,
  organization_id,
  status,
  created_at,
  started_at,
  ready_at,
  delivered_at,
  cancelled_at,
  updated_at,
  tab:tab_id (id, customer_name, server_name, table_id),
  kitchen_ticket_items (
    id,
    product_id,
    product_name,
    quantity,
    cancelled_quantity,
    notes,
    parent_kitchen_ticket_item_id,
    status,
    started_at,
    ready_at,
    delivered_at,
    products (
      product_categories ( name )
    )
  )
`;

/** Nested tab fields returned by the kitchen_tickets select embed. */
interface KitchenTicketTabEmbed {
  id: string;
  customer_name: string;
  server_name: string | null;
  table_id: string | null;
}

/** Flat kitchen_ticket_items row from the select embed. */
interface KitchenTicketItemRow {
  id: string;
  product_id: string | null;
  product_name: string;
  quantity: number;
  cancelled_quantity: number;
  notes: string | null;
  parent_kitchen_ticket_item_id: string | null;
  status: string;
  started_at: string | null;
  ready_at: string | null;
  delivered_at: string | null;
  products: {
    product_categories: { name: string } | { name: string }[] | null;
  } | Array<{
    product_categories: { name: string } | { name: string }[] | null;
  }> | null;
}

/**
 * Top-level item row used when advancing/reverting a subset. Carries
 * ready_at/delivered_at (not just started_at) so a failed ticket-status write
 * can restore the item to its exact previous state (see restoreItemsStatus).
 */
interface KitchenTicketItemStatusRow {
  id: string;
  status: string;
  started_at: string | null;
  ready_at: string | null;
  delivered_at: string | null;
  quantity: number;
  cancelled_quantity: number;
  parent_kitchen_ticket_item_id: string | null;
}

/** Ticket snapshot captured before a write, used to roll back on partial failure. */
interface KitchenTicketSnapshotRow {
  status: string;
  started_at: string | null;
  ready_at: string | null;
  delivered_at: string | null;
  cancelled_at: string | null;
}

/** kitchen_tickets row shape returned by getActiveTickets. */
interface KitchenTicketRow {
  id: string;
  tab_id: string;
  organization_id: string;
  status: string;
  created_at: string;
  started_at: string | null;
  ready_at: string | null;
  delivered_at: string | null;
  cancelled_at: string | null;
  updated_at: string;
  /** Embedded tab relation from `tab:tab_id (...)` select. */
  tab: KitchenTicketTabEmbed;
  kitchen_ticket_items: KitchenTicketItemRow[] | null;
}

export interface CreateKitchenTicketItemInput {
  tabItemId: string;
  productId?: string;
  productName: string;
  quantity: number;
  notes?: string | null;
  /** Promo bundle contents, when this line is a promo parent. */
  components?: Array<{
    componentProductId: string;
    productName: string;
    quantity: number;
  }>;
}

const categoryNameFromProduct = (
  product: KitchenTicketItemRow['products'],
): string | undefined => {
  if (!product) return undefined;
  const row = Array.isArray(product) ? product[0] : product;
  if (!row) return undefined;
  const categories = row.product_categories;
  const category = Array.isArray(categories) ? categories[0] : categories;
  return category?.name || undefined;
};

const mapKitchenStatus = (value: string): KitchenTicketStatus => {
  if (
    value === 'pending' ||
    value === 'preparing' ||
    value === 'ready' ||
    value === 'delivered' ||
    value === 'cancelled'
  ) {
    return value;
  }
  return 'pending';
};

const mapItemStatus = (value: string): KitchenTicketItemStatus => {
  if (
    value === 'pending' ||
    value === 'preparing' ||
    value === 'ready' ||
    value === 'delivered'
  ) {
    return value;
  }
  return 'pending';
};

const toDomainItemForAggregate = (row: KitchenTicketItemStatusRow) => ({
  status: mapItemStatus(row.status),
  quantity: Number(row.quantity),
  cancelledQuantity: Number(row.cancelled_quantity ?? 0),
});

const isActiveItemRow = (row: KitchenTicketItemStatusRow): boolean =>
  !row.parent_kitchen_ticket_item_id &&
  Number(row.cancelled_quantity ?? 0) < Number(row.quantity);

const persistTicketStatus = async (
  ticketId: string,
  status: KitchenTicketStatus,
  nowIso: string,
  currentStartedAt: string | null,
): Promise<{ id: string; status: KitchenTicketStatus }> => {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('kitchen_tickets')
    .update(ticketStatusUpdatePayload(status, nowIso, currentStartedAt))
    .eq('id', ticketId)
    .select('id, status')
    .single();

  if (error) throw error;
  return {
    id: data.id as string,
    status: mapKitchenStatus(data.status as string),
  };
};

const persistItemsStatus = async (
  items: KitchenTicketItemStatusRow[],
  status: KitchenTicketItemStatus,
  nowIso: string,
): Promise<void> => {
  if (items.length === 0) return;

  const supabase = getSupabaseClient();

  const groups = new Map<string, string[]>();
  for (const item of items) {
    const payload = itemStatusUpdatePayload(status, nowIso, item.started_at);
    const key = JSON.stringify(payload);
    const ids = groups.get(key) ?? [];
    ids.push(item.id);
    groups.set(key, ids);
  }

  for (const [payloadKey, ids] of groups) {
    const payload = JSON.parse(payloadKey) as Record<string, string | null>;
    const { error } = await supabase
      .from('kitchen_ticket_items')
      .update(payload)
      .in('id', ids);
    if (error) throw error;
  }
};

/** Restores items to an exact previous snapshot (compensating action after a partial failure). */
const restoreItemsStatus = async (items: KitchenTicketItemStatusRow[]): Promise<void> => {
  if (items.length === 0) return;

  const supabase = getSupabaseClient();

  const groups = new Map<string, string[]>();
  for (const item of items) {
    const payload: Record<string, string | null> = {
      status: item.status,
      started_at: item.started_at,
      ready_at: item.ready_at,
      delivered_at: item.delivered_at,
    };
    const key = JSON.stringify(payload);
    const ids = groups.get(key) ?? [];
    ids.push(item.id);
    groups.set(key, ids);
  }

  for (const [payloadKey, ids] of groups) {
    const payload = JSON.parse(payloadKey) as Record<string, string | null>;
    const { error } = await supabase
      .from('kitchen_ticket_items')
      .update(payload)
      .in('id', ids);
    if (error) throw error;
  }
};

/** Restores a ticket to an exact previous snapshot (compensating action after a partial failure). */
const restoreTicketStatus = async (
  ticketId: string,
  snapshot: KitchenTicketSnapshotRow,
): Promise<void> => {
  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from('kitchen_tickets')
    .update({
      status: snapshot.status,
      started_at: snapshot.started_at,
      ready_at: snapshot.ready_at,
      delivered_at: snapshot.delivered_at,
      cancelled_at: snapshot.cancelled_at,
    })
    .eq('id', ticketId);
  if (error) throw error;
};

/**
 * When the ticket leaves the board, persist ticket status first so a realtime
 * refetch cannot briefly re-select it as still active (delivered_at still null).
 *
 * PostgREST/Supabase-js has no cross-table transaction here, so the two writes
 * can't be truly atomic without an RPC. Instead we do a best-effort
 * compensating rollback: if the second write fails after the first succeeded,
 * we restore the already-written side back to its captured snapshot before
 * rethrowing, so a partial failure can't leave items and ticket permanently
 * out of sync.
 */
const persistItemAndTicketStatus = async (
  ticketId: string,
  items: KitchenTicketItemStatusRow[],
  itemStatus: KitchenTicketItemStatus,
  ticketStatus: KitchenTicketStatus,
  nowIso: string,
  ticketSnapshot: KitchenTicketSnapshotRow,
): Promise<{ id: string; status: KitchenTicketStatus }> => {
  const startedAt = ticketSnapshot.started_at;

  if (ticketStatus === 'delivered') {
    const result = await persistTicketStatus(ticketId, ticketStatus, nowIso, startedAt);
    try {
      await persistItemsStatus(items, itemStatus, nowIso);
    } catch (error) {
      try {
        await restoreTicketStatus(ticketId, ticketSnapshot);
      } catch (rollbackError) {
        console.error('Failed to roll back kitchen ticket status after a partial write', ticketId, rollbackError);
        Sentry.captureException(rollbackError);
      }
      throw error;
    }
    return result;
  }

  await persistItemsStatus(items, itemStatus, nowIso);
  try {
    return await persistTicketStatus(ticketId, ticketStatus, nowIso, startedAt);
  } catch (error) {
    try {
      await restoreItemsStatus(items);
    } catch (rollbackError) {
      console.error('Failed to roll back kitchen ticket item statuses after a partial write', ticketId, rollbackError);
      Sentry.captureException(rollbackError);
    }
    throw error;
  }
};

const fetchTopLevelItemRows = async (ticketId: string): Promise<KitchenTicketItemStatusRow[]> => {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('kitchen_ticket_items')
    .select('id, status, started_at, ready_at, delivered_at, quantity, cancelled_quantity, parent_kitchen_ticket_item_id')
    .eq('kitchen_ticket_id', ticketId)
    .is('parent_kitchen_ticket_item_id', null);

  if (error) throw error;
  return (data ?? []) as KitchenTicketItemStatusRow[];
};

const fetchTicketSnapshot = async (ticketId: string): Promise<KitchenTicketSnapshotRow> => {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('kitchen_tickets')
    .select('status, started_at, ready_at, delivered_at, cancelled_at')
    .eq('id', ticketId)
    .single();
  if (error) throw error;
  return data as KitchenTicketSnapshotRow;
};

const updateSelectedItemsStatus = async (
  ticketId: string,
  itemIds: string[],
  direction: 'advance' | 'revert',
): Promise<{ id: string; status: KitchenTicketStatus }> => {
  if (itemIds.length === 0) {
    throw new Error('No kitchen ticket items selected');
  }

  const rows = await fetchTopLevelItemRows(ticketId);
  const idSet = new Set(itemIds);
  const selected = rows.filter((row) => idSet.has(row.id) && isActiveItemRow(row));

  if (selected.length === 0) {
    throw new Error('No active kitchen ticket items to update');
  }

  const currentStatus = mapItemStatus(selected[0].status);
  if (selected.some((row) => mapItemStatus(row.status) !== currentStatus)) {
    throw new Error('Selected kitchen ticket items must share the same status');
  }

  const nextStatus =
    direction === 'advance' ? NEXT_STATUS[currentStatus] : PREVIOUS_STATUS[currentStatus];
  if (!nextStatus || nextStatus === 'cancelled') {
    const snapshot = await fetchTicketSnapshot(ticketId);
    return persistTicketStatus(
      ticketId,
      deriveTicketStatus(rows.map(toDomainItemForAggregate)),
      new Date().toISOString(),
      snapshot.started_at,
    );
  }

  const nowIso = new Date().toISOString();
  const derivedItems = rows.map((row) => {
    if (!idSet.has(row.id) || !isActiveItemRow(row)) return toDomainItemForAggregate(row);
    return { ...toDomainItemForAggregate(row), status: nextStatus };
  });
  const derivedStatus = deriveTicketStatus(derivedItems);
  const ticketSnapshot = await fetchTicketSnapshot(ticketId);

  return persistItemAndTicketStatus(
    ticketId,
    selected,
    nextStatus,
    derivedStatus,
    nowIso,
    ticketSnapshot,
  );
};

const mapTicketRow = (row: KitchenTicketRow): KitchenTicket => {
  const tab = row.tab;
  const rawItems = row.kitchen_ticket_items ?? [];

  const componentsByParent = new Map<string, KitchenTicketItemComponent[]>();
  for (const item of rawItems) {
    if (!item.parent_kitchen_ticket_item_id) continue;
    const list = componentsByParent.get(item.parent_kitchen_ticket_item_id) ?? [];
    list.push({
      id: item.id,
      productId: item.product_id ?? undefined,
      productName: item.product_name,
      quantity: Number(item.quantity),
    });
    componentsByParent.set(item.parent_kitchen_ticket_item_id, list);
  }

  const items: KitchenTicketItem[] = rawItems
    .filter((item) => !item.parent_kitchen_ticket_item_id)
    .map((item) => {
      const components = componentsByParent.get(item.id);
      return {
        id: item.id,
        productId: item.product_id ?? undefined,
        productName: item.product_name,
        quantity: Number(item.quantity),
        cancelledQuantity: Number(item.cancelled_quantity ?? 0),
        categoryName: categoryNameFromProduct(item.products),
        status: mapItemStatus(item.status),
        startedAt: item.started_at ? new Date(item.started_at) : null,
        readyAt: item.ready_at ? new Date(item.ready_at) : null,
        deliveredAt: item.delivered_at ? new Date(item.delivered_at) : null,
        notes: item.notes || undefined,
        components: components?.length ? components : undefined,
      };
    });

  return {
    id: row.id,
    tabId: tab.id,
    organizationId: row.organization_id,
    status: mapKitchenStatus(row.status),
    customerName: tab.customer_name ?? '',
    serverName: tab.server_name || undefined,
    tableId: tab.table_id ?? null,
    items,
    createdAt: new Date(row.created_at),
    startedAt: row.started_at ? new Date(row.started_at) : null,
    readyAt: row.ready_at ? new Date(row.ready_at) : null,
    deliveredAt: row.delivered_at ? new Date(row.delivered_at) : null,
    cancelledAt: row.cancelled_at ? new Date(row.cancelled_at) : null,
    updatedAt: new Date(row.updated_at),
  };
};

export const kitchenTicketsService = {
  async getActiveTickets(organizationId: string): Promise<KitchenTicket[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('kitchen_tickets')
      .select(KITCHEN_TICKET_SELECT)
      .eq('organization_id', organizationId)
      .is('delivered_at', null)
      .is('cancelled_at', null)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return ((data ?? []) as unknown as KitchenTicketRow[])
      .map(mapTicketRow)
      .filter(isKitchenTicketActiveOnBoard);
  },

  /**
   * Creates a single kitchen ticket for a batch of tab items (e.g. one "add to
   * tab" action). Promo components are nested under their parent line.
   *
   * Ticket + item writes are best-effort atomic: if parent or component inserts
   * fail, the ticket is deleted (items cascade) before rethrowing so the board
   * never shows an empty or incomplete ticket.
   */
  async createTicketForTabItems(
    tabId: string,
    items: CreateKitchenTicketItemInput[],
  ): Promise<void> {
    if (items.length === 0) return;

    const supabase = getSupabaseClient();

    const { data: tab, error: tabError } = await supabase
      .from('tabs')
      .select('organization_id')
      .eq('id', tabId)
      .single();

    if (tabError) throw tabError;
    if (!tab) throw new Error('Tab not found');

    const orgId = tab.organization_id as string;

    const { data: ticket, error: ticketError } = await supabase
      .from('kitchen_tickets')
      .insert({ organization_id: orgId, tab_id: tabId })
      .select('id')
      .single();

    if (ticketError) throw ticketError;

    const ticketId = ticket.id as string;

    try {
      const parentRows = items.map((item) => ({
        kitchen_ticket_id: ticketId,
        organization_id: orgId,
        tab_item_id: item.tabItemId,
        product_id: item.productId ?? null,
        product_name: item.productName,
        quantity: item.quantity,
        notes: item.notes ?? null,
      }));

      const { data: insertedParents, error: parentsError } = await supabase
        .from('kitchen_ticket_items')
        .insert(parentRows)
        .select('id, tab_item_id');

      if (parentsError) throw parentsError;

      const parentIdByTabItem = new Map<string, string>();
      for (const parent of insertedParents ?? []) {
        parentIdByTabItem.set(parent.tab_item_id as string, parent.id as string);
      }

      const componentRows = [];
      for (const item of items) {
        if (!item.components?.length) continue;
        const parentId = parentIdByTabItem.get(item.tabItemId);
        if (!parentId) continue;

        for (const comp of item.components) {
          componentRows.push({
            kitchen_ticket_id: ticketId,
            organization_id: orgId,
            tab_item_id: item.tabItemId,
            product_id: comp.componentProductId,
            product_name: comp.productName,
            quantity: comp.quantity * item.quantity,
            notes: null,
            parent_kitchen_ticket_item_id: parentId,
          });
        }
      }

      if (componentRows.length > 0) {
        const { error: compError } = await supabase
          .from('kitchen_ticket_items')
          .insert(componentRows);
        if (compError) throw compError;
      }
    } catch (error) {
      const { error: cleanupError } = await supabase
        .from('kitchen_tickets')
        .delete()
        .eq('id', ticketId);

      if (cleanupError) {
        console.error('Failed to roll back kitchen ticket', ticketId, cleanupError);
      }

      throw error;
    }
  },

  /**
   * Marks cancelled units on active (non-delivered) top-level kitchen ticket
   * lines for a tab item. Applies cancelled_quantity += removedQuantity capped
   * at each row's quantity, consuming rows in created_at, id order until
   * exhausted. No-op when the kitchen never saw the item.
   */
  async cancelTicketItemsForTabItem(
    tabItemId: string,
    removedQuantity: number,
  ): Promise<void> {
    if (removedQuantity <= 0) return;

    const supabase = getSupabaseClient();
    const maxAttempts = 5;
    let remaining = removedQuantity;

    for (let attempt = 0; attempt < maxAttempts && remaining > 0; attempt++) {
      const { data: rows, error } = await supabase
        .from('kitchen_ticket_items')
        .select(`
          id,
          quantity,
          cancelled_quantity,
          created_at,
          kitchen_tickets!inner ( delivered_at )
        `)
        .eq('tab_item_id', tabItemId)
        .is('parent_kitchen_ticket_item_id', null)
        .is('kitchen_tickets.delivered_at', null)
        .is('kitchen_tickets.cancelled_at', null)
        .order('created_at', { ascending: true })
        .order('id', { ascending: true });

      if (error) throw error;
      if (!rows?.length) return;

      let hitConflict = false;

      for (const row of rows) {
        if (remaining <= 0) break;

        const quantity = Number(row.quantity);
        const alreadyCancelled = Number(row.cancelled_quantity ?? 0);
        const available = Math.max(0, quantity - alreadyCancelled);
        if (available <= 0) continue;

        const toCancel = Math.min(available, remaining);
        const { data: updated, error: updateError } = await supabase
          .from('kitchen_ticket_items')
          .update({ cancelled_quantity: alreadyCancelled + toCancel })
          .eq('id', row.id)
          .eq('cancelled_quantity', alreadyCancelled)
          .select('id')
          .maybeSingle();

        if (updateError) throw updateError;

        if (!updated) {
          // Another writer changed this row — re-read and retry remaining.
          hitConflict = true;
          break;
        }

        remaining -= toCancel;
      }

      if (!hitConflict) return;
    }

    if (remaining > 0) {
      throw new Error(
        `Failed to allocate kitchen cancellation for tab item ${tabItemId}: ${remaining} unit(s) unapplied after retries`,
      );
    }
  },

  /**
   * Advances/reverts a ticket's status. Pass 'delivered' or 'cancelled' to
   * remove it from the active board; timestamps for each stage are stamped
   * automatically. Non-cancelled updates cascade to every active top-level item
   * so item-level status stays in sync with a whole-card advance.
   */
  async updateStatus(
    ticketId: string,
    status: KitchenTicketStatus,
  ): Promise<{ id: string; status: KitchenTicketStatus }> {
    const nowIso = new Date().toISOString();
    const ticketSnapshot = await fetchTicketSnapshot(ticketId);

    if (status === 'cancelled') {
      return persistTicketStatus(ticketId, status, nowIso, ticketSnapshot.started_at);
    }

    const rows = await fetchTopLevelItemRows(ticketId);
    const activeItems = rows.filter(isActiveItemRow);
    return persistItemAndTicketStatus(
      ticketId,
      activeItems,
      status,
      status,
      nowIso,
      ticketSnapshot,
    );
  },

  /**
   * Advances a subset of top-level items one step, then derives and persists
   * the parent ticket status from all active items.
   */
  async advanceSelectedItems(
    ticketId: string,
    itemIds: string[],
  ): Promise<{ id: string; status: KitchenTicketStatus }> {
    return updateSelectedItemsStatus(ticketId, itemIds, 'advance');
  },

  /**
   * Reverts a subset of top-level items one step, then derives and persists
   * the parent ticket status from all active items.
   */
  async revertSelectedItems(
    ticketId: string,
    itemIds: string[],
  ): Promise<{ id: string; status: KitchenTicketStatus }> {
    return updateSelectedItemsStatus(ticketId, itemIds, 'revert');
  },
};
